'use client'

import AtendimentoChatPage from '@/pages/admin/atendimento/atendimentos/atendimento-chat-page'
import { createContext, useCallback, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChatChannelIcon, chatChannelMeta } from '@/components/atendimento/chat-channel-icon'

// ==================== CONTEXT ====================

interface ChatPopupContextValue {
    openChat: (chatId: number) => void
}

const ChatPopupContext = createContext<ChatPopupContextValue>({ openChat: () => {} })

/**
 * Provider que gerencia o modal de chat.
 * Deve ser colocado FORA de qualquer DragDropContext ou container com eventos de drag.
 * Todos os ChatPopupButton dentro deste provider vao abrir o modal aqui.
 */
export function ChatPopupProvider({ children }: { children: React.ReactNode }) {
    const [chatId, setChatId] = useState<number | null>(null)

    const openChat = useCallback((id: number) => {
        setChatId(id)
    }, [])

    const closeChat = useCallback(() => {
        setChatId(null)
    }, [])

    return (
        <ChatPopupContext.Provider value={{ openChat }}>
            {children}
            {chatId !== null && createPortal(
                // Sem `data-theme`: o daisyUI aplica o fundo de página em todo elemento
                // com `[data-theme]` (`background-image: linear-gradient(...)`), e num
                // `.modal` (`fixed inset-0`) isso vira uma lâmina opaca sobre o app.
                // O tema vem do `<html>` (ver `index.html` e `useTheme`).
                <div className="modal modal-open" style={{ zIndex: 9999 }}>
                    <div
                        className="modal-box !max-w-4xl h-[85vh] p-0 flex flex-col"
                        style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
                    >
                        <div className="flex-1 overflow-hidden" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
                            <AtendimentoChatPage
                                chatIdOverride={chatId}
                                onBack={closeChat}
                            />
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop" onClick={closeChat}>
                        <button>close</button>
                    </form>
                </div>,
                document.body
            )}
        </ChatPopupContext.Provider>
    )
}

// ==================== BUTTON ====================

interface ChatPopupButtonProps {
    chatId: number
    chatStatus?: string
    /** Canal da conversa: o ícone é o do WhatsApp, do Instagram ou do Messenger. */
    channel?: string | null
    size?: number
    className?: string
    renderButton?: (open: () => void) => React.ReactNode
}

export default function ChatPopupButton({ chatId, chatStatus, channel, size = 16, className = '', renderButton }: ChatPopupButtonProps) {
    const { openChat } = useContext(ChatPopupContext)

    const handleOpen = useCallback(() => {
        openChat(chatId)
    }, [openChat, chatId])

    if (renderButton) {
        return <>{renderButton(handleOpen)}</>
    }

    return (
        <button
            className={`btn btn-ghost btn-xs btn-circle ${chatStatus === 'OPEN' ? 'text-success' : 'text-base-content/40'} ${className}`}
            title={`${chatChannelMeta(channel).label}: ${chatStatus === 'OPEN' ? 'conversa aberta, clique para conversar' : 'abrir conversa'}`}
            onClick={handleOpen}
        >
            {/* Cor do botão (aberta/fechada) vale para todos os canais; o ícone diz qual é */}
            <ChatChannelIcon channel={channel} size={size} colored={false} />
        </button>
    )
}
